import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { LEARN_ARTICLES, learnArticlePath, localizeLearnArticle } from '@/features/aprender/articles'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { article as articleUi } from '@/shared/lib/articleStyles'

export const LEARN_ESCANDALLO_EXCEL_VIEW_URL =
    'https://docs.google.com/spreadsheets/d/1zQnjZJh7NXdEWuxDJltOjK8H7Vr8yRr40j8LFLSIhSM/edit?usp=sharing'

export function TipiLink({ children = 'Tipi' }: { children?: ReactNode }) {
    return (
        <LocaleLink to='/' className={articleUi.link}>
            {children}
        </LocaleLink>
    )
}

export function DashList({
    items,
    variant = 'default',
}: {
    items: string[]
    variant?: 'default' | 'tight' | 'grid' | 'grid-sm'
}) {
    const className =
        variant === 'tight'
            ? articleUi.dashListTight
            : variant === 'grid'
              ? articleUi.dashListGrid
              : variant === 'grid-sm'
                ? articleUi.dashListGridSm
                : articleUi.dashList

    const itemClass = variant === 'grid' || variant === 'grid-sm' ? articleUi.dashItemSm : articleUi.dashItem

    return (
        <ul className={className}>
            {items.map(item => (
                <li key={item} className={itemClass}>
                    {item}
                </li>
            ))}
        </ul>
    )
}

export function Formula({
    children,
    variant = 'default',
}: {
    children: ReactNode
    variant?: 'default' | 'tight' | 'alert' | 'dark'
}) {
    const className =
        variant === 'tight'
            ? cn(articleUi.formula, articleUi.formulaTight)
            : variant === 'alert'
              ? cn(articleUi.formula, articleUi.formulaAlert)
              : variant === 'dark'
                ? articleUi.formulaDark
                : articleUi.formula

    return <div className={className}>{children}</div>
}

function formatPublishedAt(isoDate: string, locale: string) {
    return new Intl.DateTimeFormat(locale.startsWith('en') ? 'en-GB' : 'es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(new Date(`${isoDate}T12:00:00`))
}

export function LearnSeriesNav({ currentId }: { currentId: string }) {
    const { t, i18n } = useTranslation()
    const locale = useLandingLocale()
    const current = LEARN_ARTICLES.find(a => a.id === currentId)
    if (!current) return null

    const sameCategory = LEARN_ARTICLES.filter(a => a.id !== currentId && a.category === current.category)
    const fillers = LEARN_ARTICLES.filter(a => a.id !== currentId && a.category !== current.category)
    const related = [...sameCategory, ...fillers].slice(0, 3).map(item => localizeLearnArticle(item, locale))

    if (related.length === 0) return null

    return (
        <nav
            className='mx-auto mb-16 max-w-article-shell'
            aria-label={t('learn.seriesNav.ariaLabel', 'Más guías de esta serie')}
        >
            <p className='mb-6 mt-0 text-[11px] font-bold uppercase tracking-[0.14em] text-article-muted-soft'>
                {t('learn.seriesNav.keepReading', 'Seguir leyendo')}
            </p>
            <ul className='m-0 grid list-none grid-cols-1 gap-x-[clamp(20px,2.4vw,30px)] gap-y-8 p-0 sm:grid-cols-2 lg:grid-cols-3'>
                {related.map(item => (
                    <li key={item.id}>
                        <LocaleLink
                            to={learnArticlePath(item.id)}
                            className='group flex flex-col gap-3 text-article-fg no-underline'
                        >
                            <div className='overflow-hidden rounded-article-card'>
                                <img
                                    src={item.imagePath}
                                    alt=''
                                    className='aspect-[16/9] h-[150px] w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]'
                                    width={1536}
                                    height={864}
                                    loading='lazy'
                                    decoding='async'
                                />
                            </div>
                            <div className='flex min-w-0 flex-col gap-1.5'>
                                <span className='text-[11px] font-bold uppercase tracking-[0.12em] text-article-flame'>
                                    {item.category}
                                </span>
                                <span className='text-[clamp(17px,1.5vw,20px)] font-bold leading-[1.2] tracking-[-0.02em] transition-colors group-hover:text-article-flame'>
                                    {item.title}
                                </span>
                                <span className='text-[13px] text-article-muted-soft'>
                                    {formatPublishedAt(item.publishedAt, i18n.language)}
                                    {' · '}
                                    {item.readingMinutes} min
                                </span>
                            </div>
                        </LocaleLink>
                    </li>
                ))}
            </ul>
        </nav>
    )
}

export function LearnArticleLink({ slug, children }: { slug: string; children: ReactNode }) {
    return (
        <LocaleLink to={learnArticlePath(slug)} className={articleUi.link}>
            {children}
        </LocaleLink>
    )
}
